import type { Member, UsageSummary, User } from "@/types";

export const MICROCREDITS_PER_CREDIT = 1_000_000;

/** HTML 转义,防止 XSS */
export function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

/** 格式化时间,空值返回 "-" */
export function formatTime(value: string | null | undefined): string {
  if (!value) return "-";
  return new Date(value).toLocaleString();
}

/** ISO 时间 → datetime-local 输入框格式 */
export function toDatetimeLocal(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (number: number): string => String(number).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** datetime-local 输入框值 → ISO 时间字符串 */
export function fromDatetimeLocal(value: string): string | null {
  if (!value) return null;
  return new Date(value).toISOString();
}

/** 截断长 ID,只保留首尾 */
export function shortId(value: string): string {
  if (!value) return "";
  return value.length > 14 ? `${value.slice(0, 9)}...${value.slice(-4)}` : value;
}

/** 生成状态标签 HTML */
export function pill(value: string): string {
  return `<span class="pill ${escapeHtml(value)}">${escapeHtml(value)}</span>`;
}

/** 数字格式化:K / M 缩写 */
export function fmtNum(n: number): string {
  if (n >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return String(n || 0);
}

/** 整数格式化:完整展示千分位 */
export function fmtInt(n: number | null | undefined): string {
  const value = Number(n || 0);
  if (!Number.isFinite(value)) return "0";
  return Math.trunc(value).toLocaleString("zh-CN");
}

/** 钱包存储值(microcredits) -> 可读 credits */
export function formatMicrocreditsAsCredits(
  value: number | null | undefined,
  { minimumFractionDigits = 0, maximumFractionDigits = 6 }: { minimumFractionDigits?: number; maximumFractionDigits?: number } = {}
): string {
  const credits = Number(value || 0) / MICROCREDITS_PER_CREDIT;
  if (!Number.isFinite(credits)) return "0";
  return credits.toLocaleString("zh-CN", {
    minimumFractionDigits,
    maximumFractionDigits
  });
}

/** 判断成员是否已删除 */
export function isDeletedMember(member: Member): boolean {
  const status = String(member?.status || "").trim().toLowerCase();
  return Boolean(member?.deletedAt) || status === "deleted" || status === "delete";
}

/** 解析用户角色(JSON 字符串 → 数组) */
export function parseUserRoles(user: User | null): string[] {
  if (!user?.roles) return ["customer"];
  try {
    return JSON.parse(user.roles) as string[];
  } catch {
    return ["customer"];
  }
}

/** 空用量汇总 */
export function emptyUsageSummary(): UsageSummary {
  return { requests: 0, errors: 0, inputTokens: 0, outputTokens: 0, totalTokens: 0 };
}

/** 从 localStorage 读取已保存的明文 API Key(仅用于复制便利) */
export function loadStoredPlainApiKeys(): Record<string, string> {
  try {
    const raw = localStorage.getItem("deepseekproxy.plainApiKeys");
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed as Record<string, string> : {};
  } catch {
    return {};
  }
}

/** 保存明文 API Key 到 localStorage */
export function saveStoredPlainApiKeys(keys: Record<string, string>): void {
  try {
    localStorage.setItem("deepseekproxy.plainApiKeys", JSON.stringify(keys));
  } catch {
    // localStorage 仅用于便利,失败可忽略
  }
}
